import React, { useEffect, useRef, useState } from 'react';
import ActivityActionsMenu from './ActivityActionsMenu';
import ActivityHoverCard from './ActivityHoverCard';
import { ACTIVITY_COLOR, activityTimeLabel } from './activityDisplay';

/**
 * An activity on the schedule board — the case-manager copy.
 *
 * Deliberately IDENTICAL to scale-portal's
 * components/template/MediatorActivities/ActivityChip.jsx: same violet, same
 * geometry, same "time then title" order. A case manager and the mediator they
 * work for look at the same calendar for the same reasons, and an activity that
 * renders differently in the two portals reads as two different things.
 *
 * Styled inline rather than with Tailwind classes for the same reason it is in
 * scale-portal: the two apps do not share a Tailwind build, and a class that
 * exists in one and not the other fails SILENTLY — the element simply renders
 * unstyled. Inline styles cannot miss.
 *
 * THE COLOUR
 * ----------
 * Violet, and it must stay clear of all four availability states, which already
 * use green (available), grey (not available), blue (booked) and amber (on
 * hold). An activity is not an availability state: it never opens or closes a
 * slot and never blocks a booking.
 */
/* Re-exported, not defined here: the hover card needs the same colour and
 * importing it from this file would close a cycle. Several call sites already
 * import ACTIVITY_COLOR from here, so the name stays. */
export { ACTIVITY_COLOR };

/**
 * @param onOpenCase called with the activity when the chip BODY is clicked and
 *   the activity belongs to a case. An activity with no case has nowhere to go,
 *   so the chip stays inert rather than pretending to be a link.
 * @param onEdit / @param onDelete the two entries behind the "…" menu. Omit
 *   both and no menu renders.
 */
const ActivityChip = ({
  activity,
  onClick,
  onOpenCase,
  onEdit,
  onDelete,
  highlighted = false,
}) => {
  /* The SHORT title on the chip — "Pre-Med - Smith v. Acme" rather than the
   * full name. Built server-side beside display_title so the calendar entry,
   * the tracker and the email keep the full words while the narrow day cell
   * spends its width on the case name. */
  const title = activity?.short_title || activity?.display_title || 'Activity';
  /* "NA", never "All day" — these do not occupy the day. */
  const time = activityTimeLabel(activity);

  /* Replaces the native `title` tooltip, which showed the notes as unstyled
   * grey system text after a second's delay. */
  const chipRef = useRef(null);

  /* ARRIVED HERE FROM THE CASE PAGE. Clicking an activity in the case's
   * Activities list opens the board on its month with ?activity=<id>, and the
   * matching chip scrolls itself into view and pulses — landing on the right
   * month and leaving the case manager to hunt is most of why the trip felt
   * pointless. The delay lets the board finish laying out its week rows;
   * scrolling sooner lands on the wrong offset. */
    useEffect(() => {
        if (!highlighted) return undefined
        let frame
        let previousTop = null
        let stable = 0
        let attempts = 0
        const settle = () => {
            const element = chipRef.current
            if (!element) return
            const top = element.getBoundingClientRect().top
            stable = previousTop != null && Math.abs(top - previousTop) < 1 ? stable + 1 : 0
            previousTop = top
            attempts += 1
            if (stable < 5 && attempts < 120) {
                frame = requestAnimationFrame(settle)
                return
            }
            element.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' })
        }
        frame = requestAnimationFrame(settle)
        return () => cancelAnimationFrame(frame)
    }, [highlighted])
  const [hovered, setHovered] = useState(false);
  /* The "…" menu reports its own state so the card can get out of its way. */
  const [menuOpen, setMenuOpen] = useState(false);

  // The chip body opens the CASE. `onClick` is kept for callers that still
  // want the old behaviour, but `onOpenCase` wins where both are given.
  const openCase = onOpenCase && activity?.case_id ? () => onOpenCase(activity) : null;
  const bodyAction = openCase || (onClick ? () => onClick(activity) : null);
  const hasMenu = Boolean(onEdit || onDelete);

  return (
    <div
      ref={chipRef}
      role={bodyAction ? 'button' : undefined}
      tabIndex={bodyAction ? 0 : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={
        bodyAction
          ? (e) => {
              e.stopPropagation();
              bodyAction();
            }
          : undefined
      }
      onKeyDown={
        bodyAction
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                bodyAction();
              }
            }
          : undefined
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        background: ACTIVITY_COLOR.bg,
        borderRadius: '4px',
        padding: '8px 9px',
        // Without this the title (a flex item, min-width:auto) refuses to
        // shrink and pushes everything else out of the pill.
        minWidth: 0,
        cursor: bodyAction ? 'pointer' : 'default',
        fontFamily: 'inherit',
        ...(highlighted
          ? {
              outline: '1px solid #8DB5A9',
              outlineOffset: 1,
              background: '#EDF4F0',
            }
          : null),
      }}
    >
      {time && (
        <span style={{ fontSize: '12px', color: ACTIVITY_COLOR.time, flex: 'none' }}>
          {time}
        </span>
      )}
      <span
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: ACTIVITY_COLOR.fg,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          // Takes the slack so the "…" sits on the pill's right edge, where
          // the availability slot's own dots sit.
          flex: 1,
        }}
      >
        {title}
      </span>
      <ActivityHoverCard
        activity={activity}
        anchorRef={chipRef}
        open={hovered && !menuOpen}
      />
      {hasMenu && (
        <ActivityActionsMenu
          label={`Actions for ${title}`}
          onOpenChange={setMenuOpen}
          /* Both actions clear the hover state first. Each opens a dialog ON
             TOP of this chip, and a covered element never receives mouseleave
             — so `hovered` stayed true and the card floated over the dialog. */
          onEdit={
            onEdit
              ? () => {
                  setHovered(false);
                  onEdit(activity);
                }
              : undefined
          }
          onDelete={
            onDelete
              ? () => {
                  setHovered(false);
                  onDelete(activity);
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default ActivityChip;
